// Оплата через СБП: открываем приложение банка, ждём подтверждения от Т-Банка.

import { onSite } from '../lib/web.ts';

/** Куда ещё придёт сообщение, кроме экрана заказа. */
const HERE = onSite ? 'сюда и на почту' : 'сюда и в Telegram';
import { useEffect, useRef, useState } from 'react';
import { api, type OrderView } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic, openLink, shareToTelegram } from '../lib/tg.ts';
import { dayWord, rub, slotText } from '../lib/format.ts';
import { ErrorNote, Icon, Spinner, Switch, toast } from '../ui/kit.tsx';
import { isNightMsk } from './order.tsx';

const DONE = (o: OrderView, kind: 'main' | 'surcharge') =>
  kind === 'main' ? !['awaiting_payment', 'draft'].includes(o.status) : o.status !== 'awaiting_surcharge';

export function Pay({ orderId, kind = 'main' }: { orderId: string; kind?: 'main' | 'surcharge' }) {
  const { back, replace, config } = useApp();
  const [order, setOrder] = useState<OrderView | null>(null);
  const [payment, setPayment] = useState<{ paymentId: number; sbpUrl: string | null; paymentUrl: string | null; dev: boolean; manual: boolean } | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const poll = useRef<number | undefined>(undefined);

  useEffect(() => {
    api.order(orderId).then((o) => {
      setOrder(o);
      if (DONE(o, kind)) replace({ name: 'order', orderId });
    }, (e) => setError(e.message));
    return () => clearInterval(poll.current);
  }, [orderId]);

  const startPolling = () => {
    setWaiting(true);
    clearInterval(poll.current);
    poll.current = window.setInterval(async () => {
      try {
        const o = await api.order(orderId);
        setOrder(o);
        if (DONE(o, kind)) {
          clearInterval(poll.current);
          haptic.ok();
          replace({ name: 'order', orderId });
        }
      } catch {
        /* следующая попытка */
      }
    }, 2500);
  };

  const pay = async () => {
    setError(null);
    try {
      const p = payment ?? (await api.pay(orderId, kind));
      setPayment(p);
      if (p.dev) return;
      // На сайте — платёжная форма банка в этой же вкладке: после оплаты банк вернёт на страницу заказа.
      const url = onSite ? p.paymentUrl ?? p.sbpUrl : p.sbpUrl ?? p.paymentUrl;
      if (!url) throw new Error('Банк не вернул ссылку на оплату. Попробуй ещё раз');
      if (onSite) {
        location.assign(url);
        return;
      }
      openLink(url);
      startPolling();
    } catch (e: any) {
      haptic.err();
      setError(e.message);
    }
  };

  if (!order) return <main className="screen">{error ? <ErrorNote error={error} /> : <Spinner />}</main>;
  const amount = kind === 'main' ? order.total : order.surcharge ?? 0;

  if (config?.payments === 'manual') {
    return (
      <div className="sheet-bg" style={{ position: 'fixed' }}>
        <div className="sheet" role="dialog" aria-label="Оплата по ссылке">
          <span className="grip" />
          <h2 className="h2">{order.payLink ? 'Ссылка на оплату готова' : 'Заказ оформлен'}</h2>
          <p className="p">
            {order.payLink
              ? `Оплати ${rub(amount)} по ссылке. Как только оплата придёт, пришлём подтверждение и начнём готовить букет.`
              : isNightMsk()
                ? `Мы проверим заказ и пришлём ссылку на оплату ${rub(amount)} ${HERE}. Сейчас ночь, поэтому пришлём утром, после 8:00.`
                : `Мы проверим заказ и пришлём ссылку на оплату ${rub(amount)} ${HERE}, обычно в течение 15 минут.`}
          </p>
          {order.payLink ? (
            <button type="button" className="btn" onClick={() => (openLink(order.payLink!), startPolling())}>
              Оплатить {rub(amount)}
            </button>
          ) : (
            <div className="note">
              <Icon name="clock" size={18} />
              <span>Приложение можно закрыть, ссылка придёт сообщением от бота.</span>
            </div>
          )}
          <button type="button" className="btn outline" onClick={() => replace({ name: 'order', orderId })}>
            {order.payLink ? 'Оплата отправлена' : 'Понятно'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-bg" style={{ position: 'fixed' }}>
      <div className="sheet" role="dialog" aria-label="Оплата">
        <span className="grip" />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 className="h2">Оплата</h2>
          <button type="button" className="iconbtn" aria-label="Закрыть" onClick={back}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="card stack s6" style={{ fontSize: 15 }}>
          {kind === 'main' ? (
            <>
              <Line label={order.kind === 'surprise' ? `Сюрприз, ${order.size}` : order.kind === 'individual' ? 'Индивидуальный букет, L' : `Букет «${order.designName}», ${order.size}`} value={rub(order.itemPrice)} />
              {order.discount ? <Line label="Промокод" value={`−${rub(order.discount)}`} /> : null}
              <Line label={order.recipientMode === 'ask' ? 'Доставка внутри МКАД' : `Доставка, ${dayWord(order.date)} ${slotText(order.slotFrom, order.slotTo)}`} value={rub(order.deliveryPrice)} />
            </>
          ) : (
            <Line label="Доплата за доставку за МКАД" value={rub(amount)} />
          )}
          <div className="row" style={{ justifyContent: 'space-between', fontWeight: 700, paddingTop: 8, borderTop: '1px solid #F1EDE0' }}>
            <span>Итого</span>
            <span className="price">{rub(amount)}</span>
          </div>
        </div>
        <div className="card row">
          <span style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--butter)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flex: 'none' }}>СБП</span>
          <span className="stack s6" style={{ gap: 0 }}>
            <span style={{ fontWeight: 500 }}>{onSite ? 'Картой или через СБП' : 'Оплата через СБП'}</span>
            <span className="tiny">{onSite ? 'Откроется защищённая страница Т-Банка' : 'Откроется приложение твоего банка, без комиссии'}</span>
          </span>
        </div>
        <ErrorNote error={error} />
        {waiting ? (
          <div className="stack center" style={{ alignItems: 'center' }}>
            <div className="spinner" />
            <span className="small">Ждём подтверждение от банка…</span>
            <button type="button" className="quiet" style={{ textDecoration: 'underline' }} onClick={pay}>
              Открыть банк ещё раз
            </button>
          </div>
        ) : (
          <button type="button" className="btn" onClick={pay}>
            {onSite ? 'Оплатить картой или СБП' : 'Оплатить через СБП'} · {rub(amount)}
          </button>
        )}
        {payment?.dev && (
          <div className="note pink stack" style={{ display: 'flex' }}>
            <span>Тестовый режим: банк не подключён. Выбери результат оплаты.</span>
            <div className="row">
              <button type="button" className="btn sm" style={{ flex: 1 }} onClick={() => api.devPay(payment.paymentId).then(startPolling)}>
                Успех
              </button>
              <button type="button" className="btn sm outline" style={{ flex: 1 }} onClick={() => api.devPay(payment.paymentId, true).then(() => setError('Оплата не прошла, деньги не списались. Попробуй ещё раз или выбери другой банк'))}>
                Ошибка
              </button>
            </div>
          </div>
        )}
        <span className="row tiny" style={{ justifyContent: 'center', gap: 6 }}>
          <Icon name="info" size={14} />
          Платёж защищён банком. Чек придёт {onSite ? 'на почту' : 'на телефон'}.
        </span>
      </div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--ink-2)' }}>{label}</span>
      <span className="price" style={{ whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );
}

/** Блок «пригласить получателя» — для подарка, где адрес выбирает получатель.
 *  Можно отправить ссылку самому или попросить нас связаться с получателем (анонимно). */
export function ShareRecipient({ order, onChange }: { order: OrderView; onChange?: (o: OrderView) => void }) {
  const [busy, setBusy] = useState(false);
  if (!order.recipientLinks) return null;
  const text = 'Тебе приготовили цветы! Выбери, куда и когда их привезти:';
  if (order.contactByUs)
    return (
      <div className="status">
        <Icon name="check" size={20} width={2.6} />
        Свяжемся с получателем сами, сюрприз не испортим
      </div>
    );
  return (
    <div className="stack">
      <span className="label">Ссылка для получателя</span>
      {onSite && (
        <span className="card" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 15, wordBreak: 'break-all', userSelect: 'all' }}>
          {order.recipientLinks.web}
        </span>
      )}
      {onSite ? (
        typeof navigator.share === 'function' && (
          <button type="button" className="btn" onClick={() => navigator.share({ text: `${text} ${order.recipientLinks!.web}` }).catch(() => {})}>
            <Icon name="send" size={18} color="#FEEB9E" />
            Поделиться в мессенджере
          </button>
        )
      ) : (
        <button type="button" className="btn" onClick={() => shareToTelegram(order.recipientLinks!.telegram, text)}>
          <Icon name="send" size={18} color="#FEEB9E" />
          Отправить в Telegram
        </button>
      )}
      <button
        type="button"
        className="btn light"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(`${text} ${order.recipientLinks!.web}`);
            toast('Ссылка скопирована');
          } catch {
            toast(order.recipientLinks!.web);
          }
        }}
      >
        <Icon name="copy" size={18} />
        Скопировать ссылку
      </button>
      <span className="tiny">{onSite ? 'По ссылке получатель выберет адрес и время. Если он долго не ответит, мы свяжемся с ним сами.' : 'Ссылка открывается и в обычном браузере. Если получатель долго не ответит, мы свяжемся с ним сами.'}</span>
      <button
        type="button"
        className="btn outline"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const o = await api.contactRecipient(order.publicId);
            haptic.ok();
            onChange?.(o);
          } catch (e: any) {
            toast(e.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        Свяжитесь с получателем сами
      </button>
      <span className="tiny">Если хочешь остаться инкогнито, мы сами напишем или позвоним получателю.</span>
    </div>
  );
}

export function NotifyToggle() {
  const { me, reloadMe } = useApp();
  const [on, setOn] = useState(me?.user.notifyOrders ?? true);
  return (
    <div className="row" style={{ background: 'var(--pink)', borderRadius: 20, padding: '12px 16px', justifyContent: 'space-between' }}>
      <span className="stack s6" style={{ gap: 2 }}>
        <span style={{ fontWeight: 600 }}>Сообщать о заказе в Telegram</span>
        <span className="tiny" style={{ color: 'var(--pink-ink-2)' }}>
          Фото букета перед отправкой, курьер в пути, вручено
        </span>
      </span>
      <Switch
        label="Сообщать о заказе"
        on={on}
        onChange={async (v) => {
          setOn(v);
          await api.updateMe({ notifyOrders: v }).catch(() => setOn(!v));
          reloadMe();
        }}
      />
    </div>
  );
}
